import {
  collection,
  doc,
  addDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from './config';

export type ChecklistItemStatus = 'PASS' | 'FAIL' | 'NA' | 'NEEDS_REVIEW';

export interface ChecklistItemComputedChecks {
  documentationOk?: boolean | null;
  documentationReason?: string | null;
  timely?: boolean | null;
  timelyReason?: string | null;
  dueDate?: any | null; // Date (Firestore Timestamp or Date)
  eventDate?: any | null; // Date (Firestore Timestamp or Date)
}

export interface ChecklistItemResult {
  status: ChecklistItemStatus; // PASS, FAIL, NA, NEEDS_REVIEW
  findingText?: string; // Reviewer narrative
  regulationTags?: string[]; // Optional regulation tags (can remain manual)
  updatedBy?: string; // User ID who last updated this item result
  updatedAt?: any; // Timestamp when item result was last updated
  // Computed checks for future automation
  computedChecks?: ChecklistItemComputedChecks;
}

export interface ChecklistCompletion {
  id?: string;
  checklistId: string;
  checklistName?: string;
  locationId?: string;
  locationName?: string;
  assetId?: string; // Asset ID when checklist is run from asset page
  assetName?: string; // Asset name for display in UI and PDFs
  variableValues?: Record<string, string>; // variable name -> value
  photos?: Record<string, string | string[]>; // item text -> photo URL(s) - can be single URL or array of URLs
  items?: Record<string, boolean>; // item text -> completed (legacy format, kept for backward compatibility)
  itemResults?: Record<string, ChecklistItemResult>; // item text -> structured result (new format)
  itemTaskReferences?: Record<string, string[]>; // item text -> array of task IDs created from this item
  completedBy?: string; // User ID
  completedByName?: string; // Formatted display name (e.g., "Name (email)")
  completedAt?: any; // Primary timestamp
  createdAt?: any;
  updatedAt?: any; // Last update timestamp
  organizationId?: string;
  owner_role?: string; // Owner role from compliance template for access control
  source?: 'web' | 'mobile'; // Source of the completion - web or mobile app
  pdfUrl?: string;
  pdfStoragePath?: string;
  pdfGeneratedAt?: string;
  // Time tracking fields (new)
  startedAt?: any; // When checklist was first started
  activeTimeSeconds?: number; // Total active time (excluding pauses)
  totalTimeSeconds?: number; // Total time from start to completion (including pauses)
  pauseCount?: number; // Number of times checklist was paused
  runId?: string; // Reference back to checklist_runs document (if run was used)
  // Completion status (new)
  status?: 'complete' | 'paused' | 'pending_review' | 'in_progress'; // Completion status
  pendingItems?: string[]; // Array of item texts that are pending (NEEDS_REVIEW or not satisfied)
  completionSummary?: {
    totalItems: number;
    completedItems: number;
    incompleteItems: number;
    missingRequiredFields: Array<{
      itemText: string;
      missingFields: string[];
    }>;
    missingRequiredEvidence: Array<{
      itemText: string;
      evidenceCategory: string;
    }>;
    needsReviewItems: string[];
  };
}

export const checklistCompletionService = {
  // Add a checklist completion record
  // Uses mobile-compatible format: items, photos, completedAt
  // Also supports new structured format: itemResults
  addCompletion: async (
    completionData: Omit<ChecklistCompletion, 'id' | 'completedAt' | 'createdAt'>
  ) => {
    try {
      const completionsRef = collection(db, 'checklist_completions');
      
      // Sync itemResults with legacy items format for backward compatibility
      let items = completionData.items;
      let itemResults = completionData.itemResults;
      
      // If itemResults provided but no items, generate items from itemResults
      if (itemResults && !items) {
        items = {};
        Object.entries(itemResults).forEach(([itemText, result]) => {
          // Map status to boolean: PASS/NA = true, FAIL/NEEDS_REVIEW = false
          items![itemText] = result.status === 'PASS' || result.status === 'NA';
        });
      }
      
      // If items provided but no itemResults, generate itemResults from items
      if (items && !itemResults) {
        itemResults = {};
        Object.entries(items).forEach(([itemText, completed]) => {
          itemResults![itemText] = {
            status: completed ? 'PASS' : 'FAIL',
          };
        });
      }

      const payload: Record<string, any> = {
        ...completionData,
        // Use synced values
        ...(items && { items }),
        ...(itemResults && { itemResults }),
        // Trim whitespace from locationName if provided
        ...(completionData.locationName && { locationName: completionData.locationName.trim() }),
        completedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      };

      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      const docRef = await addDoc(completionsRef, payload);
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding checklist completion:', error);
      return { success: false, error: error.message };
    }
  },

  attachPdf: async (
    completionId: string,
    data: { pdfUrl: string; pdfStoragePath?: string; pdfGeneratedAt?: string }
  ) => {
    try {
      const completionRef = doc(db, 'checklist_completions', completionId);
      await updateDoc(completionRef, {
        pdfUrl: data.pdfUrl,
        ...(data.pdfStoragePath && { pdfStoragePath: data.pdfStoragePath }),
        pdfGeneratedAt: data.pdfGeneratedAt || new Date().toISOString(),
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error: any) {
      console.error('Error attaching checklist PDF:', error);
      return { success: false, error: error.message };
    }
  },

  // Update a specific checklist item result
  updateItemResult: async (
    completionId: string,
    itemText: string,
    itemResult: Partial<ChecklistItemResult>,
    updatedBy?: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const completionRef = doc(db, 'checklist_completions', completionId);
      
      // Get current completion to merge with updates
      const { getDoc } = await import('firebase/firestore');
      const completionSnap = await getDoc(completionRef);
      
      if (!completionSnap.exists()) {
        return { success: false, error: 'Checklist completion not found' };
      }

      const currentData = completionSnap.data() as ChecklistCompletion;
      const currentItemResults = currentData.itemResults || {};
      
      // Get existing item result or create new one
      const existingResult = currentItemResults[itemText] || {
        status: 'PASS' as ChecklistItemStatus,
      };

      // Merge updates
      const updatedResult: ChecklistItemResult = {
        ...existingResult,
        ...itemResult,
        updatedBy: updatedBy || existingResult.updatedBy,
        updatedAt: serverTimestamp(),
      };

      // Update itemResults map
      const updatedItemResults = {
        ...currentItemResults,
        [itemText]: updatedResult,
      };

      // Also update legacy items map for backward compatibility
      const currentItems = currentData.items || {};
      const updatedItems = {
        ...currentItems,
        [itemText]: itemResult.status === 'PASS' || itemResult.status === 'NA',
      };

      await updateDoc(completionRef, {
        itemResults: updatedItemResults,
        items: updatedItems, // Keep in sync for backward compatibility
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error updating checklist item result:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a single checklist completion by ID
  getCompletion: async (completionId: string): Promise<{ success: boolean; data?: ChecklistCompletion; error?: string }> => {
    try {
      const completionRef = doc(db, 'checklist_completions', completionId);
      const { getDoc } = await import('firebase/firestore');
      const docSnap = await getDoc(completionRef);
      
      if (docSnap.exists()) {
        return { success: true, data: { id: docSnap.id, ...docSnap.data() } as ChecklistCompletion };
      } else {
        return { success: false, error: 'Checklist completion not found' };
      }
    } catch (error: any) {
      console.error('Error getting checklist completion:', error);
      return { success: false, error: error.message };
    }
  },

  // Update entire checklist completion
  updateCompletion: async (
    completionId: string,
    updates: Partial<ChecklistCompletion>
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const completionRef = doc(db, 'checklist_completions', completionId);
      
      // Remove undefined values
      const cleanedUpdates: Record<string, any> = {};
      Object.keys(updates).forEach(key => {
        if (updates[key as keyof ChecklistCompletion] !== undefined) {
          cleanedUpdates[key] = updates[key as keyof ChecklistCompletion];
        }
      });

      await updateDoc(completionRef, {
        ...cleanedUpdates,
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error updating checklist completion:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all completions for a specific checklist
  getCompletionsForChecklist: async (checklistId: string) => {
    try {
      const completionsRef = collection(db, 'checklist_completions');
      // Remove orderBy to avoid requiring an index
      const q = query(
        completionsRef,
        where('checklistId', '==', checklistId)
      );
      const querySnapshot = await getDocs(q);

      const completions: ChecklistCompletion[] = [];
      querySnapshot.forEach((doc) => {
        completions.push({ id: doc.id, ...doc.data() } as ChecklistCompletion);
      });

      // Sort by completedAt in memory (descending)
      completions.sort((a, b) => {
        const dateA = a.completedAt?.toDate ? a.completedAt.toDate().getTime() : 
                     (a.completedAt instanceof Date ? a.completedAt.getTime() :
                     (typeof a.completedAt === 'number' ? a.completedAt : 0));
        const dateB = b.completedAt?.toDate ? b.completedAt.toDate().getTime() : 
                     (b.completedAt instanceof Date ? b.completedAt.getTime() :
                     (typeof b.completedAt === 'number' ? b.completedAt : 0));
        return dateB - dateA;
      });

      return { success: true, data: completions };
    } catch (error: any) {
      console.error('Error getting checklist completions:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all checklist completions
  getAllCompletions: async () => {
    try {
      const completionsRef = collection(db, 'checklist_completions');
      const querySnapshot = await getDocs(completionsRef);

      const completions: ChecklistCompletion[] = [];
      querySnapshot.forEach((doc) => {
        completions.push({ id: doc.id, ...doc.data() } as ChecklistCompletion);
      });

      // Sort by completedAt in memory (descending)
      completions.sort((a, b) => {
        const dateA = a.completedAt?.toDate ? a.completedAt.toDate().getTime() : 
                     (a.completedAt instanceof Date ? a.completedAt.getTime() :
                     (typeof a.completedAt === 'number' ? a.completedAt : 0));
        const dateB = b.completedAt?.toDate ? b.completedAt.toDate().getTime() : 
                     (b.completedAt instanceof Date ? b.completedAt.getTime() :
                     (typeof b.completedAt === 'number' ? b.completedAt : 0));
        return dateB - dateA;
      });

      return { success: true, data: completions };
    } catch (error: any) {
      console.error('Error getting all checklist completions:', error);
      return { success: false, error: error.message };
    }
  },

  // Get completions by location
  getCompletionsByLocation: async (locationId: string) => {
    try {
      const completionsRef = collection(db, 'checklist_completions');
      const q = query(
        completionsRef,
        where('locationId', '==', locationId)
      );
      const querySnapshot = await getDocs(q);

      const completions: ChecklistCompletion[] = [];
      querySnapshot.forEach((doc) => {
        completions.push({ id: doc.id, ...doc.data() } as ChecklistCompletion);
      });

      // Sort by completedAt in memory (descending)
      completions.sort((a, b) => {
        const dateA = a.completedAt?.toDate ? a.completedAt.toDate().getTime() : (typeof a.completedAt === 'number' ? a.completedAt : (a.completedAt instanceof Date ? a.completedAt.getTime() : 0));
        const dateB = b.completedAt?.toDate ? b.completedAt.toDate().getTime() : (typeof b.completedAt === 'number' ? b.completedAt : (b.completedAt instanceof Date ? b.completedAt.getTime() : 0));
        return dateB - dateA;
      });

      return { success: true, data: completions };
    } catch (error: any) {
      console.error('Error getting completions by location:', error);
      return { success: false, error: error.message };
    }
  },

  // Get checklist completions for a specific asset
  getCompletionsForAsset: async (assetId: string) => {
    try {
      const completionsRef = collection(db, 'checklist_completions');
      const querySnapshot = await getDocs(completionsRef);

      const completions: ChecklistCompletion[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const completion = { id: doc.id, ...data } as ChecklistCompletion;
        
        // Check if assetId matches in various possible locations
        const matchesAsset = 
          completion.assetId === assetId ||
          (completion.variableValues && completion.variableValues['assetId'] === assetId) ||
          (completion.variableValues && completion.variableValues['asset'] === assetId);
        
        if (matchesAsset) {
          completions.push(completion);
        }
      });

      // Sort by completedAt in memory (descending)
      completions.sort((a, b) => {
        const dateA = a.completedAt?.toDate ? a.completedAt.toDate().getTime() : (typeof a.completedAt === 'number' ? a.completedAt : (a.completedAt instanceof Date ? a.completedAt.getTime() : 0));
        const dateB = b.completedAt?.toDate ? b.completedAt.toDate().getTime() : (typeof b.completedAt === 'number' ? b.completedAt : (b.completedAt instanceof Date ? b.completedAt.getTime() : 0));
        return dateB - dateA;
      });

      return { success: true, data: completions };
    } catch (error: any) {
      console.error('Error getting checklist completions for asset:', error);
      return { success: false, error: error.message };
    }
  },
};

